import {EventEmitter, Injectable} from "@angular/core";
import {PortalService} from "./portal.service";

/**
 * Service with helper functions for embedded advisors
 */
@Injectable({
  providedIn: 'root'
})
export class EmbedService {

  /**
   * Hold the name of some parameter, set by the embed
   */
  public static readonly PARAMETER_CID = "cid";
  public static readonly PARAMETER_CAMPAIGNID = "campaignid";

  /**
   * HOlds to current bodyheight
   */
  bodyHeight: number = 0;

  /**
   * Holds the conmtainer if where the advisor is embeddedn (id of the iframe)
   */
  containerId: string;

  /**
   * Holds the campaignid if it was set in the url
   */
  campaignId: string = null;

  /**
   * toggle if scrolling of the iframe is allowed
   */
  forceScroll: boolean = false;

  /**
   * Holds the current offset of the frame if embedded
   */
  public currentOffset: number = 0;

  /**
   * stores the available screenheight
   */
  public availableHeight: number = 0;

  /**
   * Holds the external URL where the advisors is embeded
   */
  public externalUrl: string = null;

  /**
   * Eventhandler for scroll events that are triggered from outside
   */
  public scrollEvent: EventEmitter<{ position: number, offset: number }> = new EventEmitter();

  /**
   * Event that is fired if the exit intend event was given by a external form.
   */
  public exitEvent: EventEmitter<boolean> = new EventEmitter();

  /**
   * constructor
   * @param portalService
   */
  constructor(private portalService: PortalService) {}

  /**
   * initializes the embedservice. Checks if the current advisor is embedded.
   */
  init(): void {
    if(typeof URLSearchParams === "function") {
      // try to get the containerId from the url
      const urlParams = new URLSearchParams(document.location.search);
      try {
        if (urlParams.has(EmbedService.PARAMETER_CID)) {
          this.containerId = urlParams.get(EmbedService.PARAMETER_CID);
          if (typeof localStorage === "object") {
            localStorage.setItem('advisor-cid', this.containerId);
          }
        } else if (typeof localStorage === "object" && localStorage.getItem('advisor-cid')) {
          this.containerId = localStorage.getItem('advisor-cid');
        }
      } catch (e) {
        if (urlParams.has(EmbedService.PARAMETER_CID)) {
          this.containerId = urlParams.get(EmbedService.PARAMETER_CID);
        }
      }

      // extract campaignId
      if (urlParams.has(EmbedService.PARAMETER_CAMPAIGNID)) {
        this.campaignId = urlParams.get(EmbedService.PARAMETER_CAMPAIGNID);
      }
    }

    // start sending resize message to the parent only if we have a parent
    if(this.isEmbedded()) {
      this.initResizeObserver();

      // get external URL
      this.getUrl();

      // listen for message from parent
      window.addEventListener('message', event => this.listen(event));

      // add extra class to html element
      const root = document.getElementsByTagName( 'html' )[0];
      root.classList.add('embedded');

      // ad scroll class if scrolling is forced
      if(this.portalService.getConfigAsBool('embed.forcescroll') === true) {
        this.forceScroll = true;
        root.classList.add('scroll');
      }
    }
  }

  /**
   * check if this is the initial call of the advisor and there was no navigation between steps
   */
  isInitialCall(): boolean {
    const urlParams = new URLSearchParams(document.location.search);
    return urlParams.has('cid');
  }


  /**
   * Listens for message from the parent
   * @param event
   */
  listen(event): void {
    if(event && event.data && event.data.hasOwnProperty("type")) {
      switch(event.data.type) {
        case "scroll":
          this.currentOffset = event.data.offset;
          this.availableHeight = event.data.height;
          this.scrollEvent.emit({position: event.data.value, offset: event.data.offset});
          break;
        case "seturl":
          this.externalUrl = event.data.value;
          break;
        case "exit":
          // show feedbackform on exit intend
          this.exitEvent.emit(true);
          break;
      }
    }
  }

  /**
   * Returns the exit intend event emitter
   */
  getExitEmitter(): EventEmitter<boolean> {
    return this.exitEvent;
  }

  /**
   * Listen for resize events and send resize message to parent if there is one
   */
  initResizeObserver(): void {
    const elementToObserve = document.querySelector("body");
    if (!elementToObserve) {
      // stop here, element not found
      return;
    }

    // fallback for older browser
    if (!window.ResizeObserver) {
      window.setInterval(() => {
        const height = elementToObserve.offsetHeight;

        // only send the message if the size has changed
        if(height !== this.bodyHeight) {
          this.bodyHeight = height;
          window.parent.postMessage({
            type: "size",
            value: this.bodyHeight,
            cid: this.containerId
          }, "*");
        }
      }, 500);
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length > 0) {
        const height = entries[0].contentRect.height;
        // send a message if the height changes
        if (height !== this.bodyHeight) {
          this.bodyHeight = height;
          parent.postMessage({
            type: "size",
            value: this.bodyHeight,
            cid: this.containerId
          }, "*");
        }
      }
    });

    resizeObserver.observe(elementToObserve);
  }

  /**
   * tells the embedding page to scroll to the top
   */
  scrollToTop(offset: number = 0): void {
    // only scroll to the element if it is really visible.
    if(this.isEmbedded() && (offset + this.currentOffset < 0 || Math.abs(offset + this.currentOffset) > this.availableHeight)) {
      this.scroll(offset, "scrollToTop");
    }
  }

  /**
   * tell page to scroll to current position + offset
   * @param offset
   * @param message
   */
  scroll(offset: number, message: string = "scroll"): void {
    if(this.isEmbedded()) {
      window.parent.postMessage({
        "type": message,
        "value": offset,
        "cid": this.containerId
      }, "*");
    }

    // if scrolling inside the iframe is allowed, scroll to the element.
    if(this.forceScroll === true) {
      window.scroll({top: offset});
    }
  }

  /**
   * triggers a 0px scrollevent on the embeddingpage
   */
  triggerFakeScroll(): void {
    if(this.isEmbedded()) {
      window.parent.postMessage({
        "type": "fakescroll",
        "cid": this.containerId
      }, "*");
    }
  }

  /**
   * try to get the url from the sorrounding page
   */
  getUrl(): void {
    if(this.isEmbedded()) {
      window.parent.postMessage({
        "type": "geturl",
        "cid": this.containerId
      }, "*");
    }
  }

  /**
   * retruns if the advisor is embedded or not.
   */
  isEmbedded(): boolean {
    return window.parent && window !== window.parent;
  }

  /**
   * get the current campaignId
   */
  getCampaignId(): string {
    return this.campaignId;
  }
}
